import React, { useEffect, useMemo, useState } from "react";
import Icon from "../shared/Icon";
import { createResident } from "../../../services/managerApi";

/**
 * Création d'un résident depuis la liste.
 *
 * Seul le nom est obligatoire. L'email est facultatif : sur les 9 180 résidents
 * en production, 76 % portent un email généré — un résident de maison de repos
 * ne se connecte jamais, et l'exiger bloquerait la saisie pour rien.
 *
 * Le rattachement part par identifiant de point de collecte quand le serveur
 * a renvoyé le périmètre du manager : c'est de cet identifiant que la société
 * est déduite, après vérification que le manager en est responsable.
 *
 * Le champ n'est jamais une saisie libre : un manager ne doit pouvoir créer un
 * résident que dans SES établissements, et un nom tapé à la main ne prouve
 * rien. Tant que `/customer/manager/scope` répond 404, ce formulaire n'a donc
 * pas de rattachement à fournir — son point d'entrée est masqué en amont.
 */
/**
 * Les trois identifiants les plus hauts déjà attribués.
 *
 * Affichés sous le champ à titre de repère, pas de proposition. Les maisons
 * numérotent leurs résidents en série — LOT613, LOT614, LOT615 — mais seuls 17
 * des 40 établissements de production ont un motif détectable, et deviner pour
 * les autres produirait des références qui n'ont de sens pour personne. Montrer
 * les faits laisse le manager écrire la suite lui-même, ou tout autre chose.
 */
const recentWoshIds = (residents = []) =>
  residents
    .map((resident) => resident.woshUserId)
    .filter(Boolean)
    // Tri naturel : sans l'option numérique, « LOT99 » passerait après « LOT615 ».
    .sort((a, b) => String(b).localeCompare(String(a), undefined, { numeric: true }))
    .slice(0, 3);

const AddResidentForm = ({ authUser, collectionPoints, residents, onCreated, onCancel, t }) => {
  // Le manager ne peut rattacher un résident qu'aux établissements dont il est
  // effectivement responsable. Avec un seul, la liste n'a qu'une entrée, déjà
  // sélectionnée — le champ reste visible pour dire à quoi on rattache.
  const places = useMemo(() => collectionPoints || [], [collectionPoints]);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    woshUserId: "",
    collectionPointId: places[0]?.collection_point_id ?? "",
    residence: places[0]?.name || "",
  });
  const [state, setState] = useState(null);

  // Les établissements arrivent du profil, après le premier rendu : on
  // renseigne le rattachement dès qu'ils sont là, sans écraser un choix.
  useEffect(() => {
    if (!places.length) return;
    setForm((current) => {
      if (current.collectionPointId !== "" || current.residence) return current;
      return {
        ...current,
        collectionPointId: places[0].collection_point_id ?? "",
        residence: places[0].name || "",
      };
    });
  }, [places]);

  const set = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const isValid = form.lastName.trim().length > 0;
  const recent = useMemo(() => recentWoshIds(residents), [residents]);

  const submit = async (event) => {
    event.preventDefault();
    if (!isValid) return;

    setState({ status: "saving" });
    const result = await createResident({
      authUser,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      woshUserId: form.woshUserId.trim(),
      collectionPointId: form.collectionPointId === "" ? null : form.collectionPointId,
    });

    if (result.ok) {
      // Le serveur peut ignorer l'identifiant demandé et générer le sien. Le
      // dire vaut mieux que de laisser le manager découvrir sur une étiquette
      // que sa numérotation n'a pas été suivie.
      const requested = form.woshUserId.trim();
      const assigned = result.data?.wosh_user_id || result.data?.woshUserId || null;
      const ignored = requested && assigned && assigned !== requested;

      setState({ status: "saved", ignored: ignored ? { requested, assigned } : null });
      onCreated(result.data);
      return;
    }
    setState({ status: "error", reason: result.reason, detail: result.detail });
  };

  return (
    <form className="mgr-card mgr-card-pad mgr-addres" onSubmit={submit}>
      <div className="mgr-card-title">{t("Bewoner toevoegen")}</div>

      <div className="mgr-addres-grid">
        <label>
          <span>{t("First name")}</span>
          <input value={form.firstName} onChange={set("firstName")} autoFocus />
        </label>
        <label>
          <span>
            {t("Last Name")} <span className="mgr-addres-req">*</span>
          </span>
          <input value={form.lastName} onChange={set("lastName")} required />
        </label>
        <label>
          <span>{t("Residence/Company")}</span>
          {places.length > 1 ? (
            <select value={form.collectionPointId} onChange={set("collectionPointId")}>
              {places.map((place) => (
                <option key={place.collection_point_id ?? place.name} value={place.collection_point_id ?? ""}>
                  {place.name}
                </option>
              ))}
            </select>
          ) : (
            // Un seul établissement — ou aucun connu, ce qui est le cas tant que
            // le serveur ne renvoie pas le périmètre. Champ libre dans les deux
            // cas : sans lui, il n'y aurait aucun moyen de nommer la résidence.
            <input value={form.residence} readOnly aria-readonly="true" />
          )}
        </label>
        <label>
          <span>{t("User ID")}</span>
          <input
            value={form.woshUserId}
            onChange={set("woshUserId")}
            placeholder={t("Optioneel — de server maakt er zelf een aan.")}
          />
          {recent.length ? (
            <small>
              {t("Reeds toegekend")} : {recent.join(", ")}
            </small>
          ) : null}
        </label>
        <label>
          <span>{t("E-mail")}</span>
          <input type="email" value={form.email} onChange={set("email")} />
          <small>{t("Optioneel — de server maakt er zelf een aan.")}</small>
        </label>
      </div>

      {state?.status === "saved" && state.ignored ? (
        <div className="mgr-note">
          <Icon name="alert-circle" style={{ fontSize: 20, color: "var(--mgr-brand)" }} />
          <div>
            <div className="mgr-note-title">{t("Andere gebruikers-ID toegekend")}</div>
            {t("De server heeft {{assigned}} toegekend in plaats van {{requested}}.", state.ignored)}
          </div>
        </div>
      ) : null}

      {state?.status === "error" ? (
        <div className="mgr-error" style={{ marginTop: 4 }}>
          <Icon name="alert-circle" style={{ fontSize: 20, flexShrink: 0 }} />
          <span>
            {state.reason === "not-implemented"
              ? t(
                  "De bewoner kon niet aangemaakt worden: de server biedt hiervoor nog geen eindpunt aan."
                )
              : `${t("De bewoner kon niet aangemaakt worden.")}${
                  state.detail ? ` (${state.detail})` : ""
                }`}
          </span>
        </div>
      ) : null}

      <div className="mgr-addres-actions">
        <button
          type="button"
          className="mgr-btn-ghost"
          onClick={onCancel}
          disabled={state?.status === "saving"}
        >
          {t("Annuleren")}
        </button>
        <button
          type="submit"
          className="mgr-btn-primary"
          disabled={!isValid || state?.status === "saving"}
        >
          <Icon name="person-add" style={{ fontSize: 18 }} />
          {state?.status === "saving" ? t("Bezig…") : t("Bewoner toevoegen")}
        </button>
      </div>
    </form>
  );
};

export default AddResidentForm;
